import React from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Divider } from '@mui/material';
import PageContainer from 'components/PageContainer';
import { ADVISER, createAdviserEditRoute } from 'adminConstants/routes';
import AdviserFeeAmendments from './AdviserFeeAmendments';
import AdviserBasicDetails from './AdviserBasicDetails';
import { useAdviser } from 'hooks/data/adviser/useAdviser';
import AdviserInvestments from './AdviserInvestments';
import OrganisationDetails from './OrganisationDetails';
import Notes from 'components/Notes';
import { NoteRelation } from 'further-types/notes';
import PageContent from 'components/PageContent';
import Button from 'components/Button';
import RecordView from 'components/RecordView';
import Heading from 'components/Heading';
import PageHeader from 'components/PageHeader';
import ResponsiveActions from 'components/PageHeader/ResponsiveActions';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { useGetRole } from 'hooks/ui/useGetRole';
import DownloadStatementMenu from 'components/DownloadStatementMenu';
import { useLaunchPortalImpersonation } from 'hooks/data/impersonationAccess/useLaunchPortalImpersonation';
import AlertDialog from 'components/AlertDialog';
import { useDisclosure } from 'further-ui/hooks';
import { FirmSelector } from 'components/FirmSelector';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import api from 'lib/trpcClient';
import { CalendarDay } from 'further-ui/utils';

const breadcrumbs = [
  { label: 'Dashboard' },
  { label: 'Adviser', link: ADVISER },
  { label: 'View Adviser', link: '/', isActive: true },
];

const title = 'View adviser';

const ViewAdviser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const adviser = useAdviser({ params: { id } });
  const { isSuperAdmin, firmId } = useGetRole();
  const impersonationDisclosure = useDisclosure();
  const launchPortalImpersonation = useLaunchPortalImpersonation();
  const [impersonatePermission, canEditAdviser] = useHasPermissions([
    RolePermission.UseImpersonationAccessAdviser,
    RolePermission.EditAdvisers,
  ]);
  const adviserData = adviser?.fetch?.data?.adviser || {};
  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const { data: eisFirm } = api.eisFirm.getById.useQuery(
    { firmId: firmId ?? '' },
    { enabled: !!firmId },
  );

  const handleImpersonation = () => {
    if (isSuperAdmin) {
      impersonationDisclosure.onOpen();
    } else {
      launchPortalImpersonation.adviser({
        adviserId: id,
        firmDomain: eisFirm?.domain ?? '',
      });
    }
  };

  const handleAdviserStatement = (valuationDate?: Date) => {
    createBackgroundExport.mutate({
      exportType: isSuperAdmin
        ? BackgroundExportTypes.AdviserStatementExportAllFirms
        : BackgroundExportTypes.AdviserStatementExport,
      params: {
        adviserId: id,
        valuationDate: valuationDate
          ? new CalendarDay(valuationDate)
          : undefined,
      },
    });
  };

  return (
    <PageContainer
      heading={title}
      breadcrumbs={breadcrumbs}
      loading={!!id && adviser.fetch.isLoading}
    >
      <PageContent>
        <PageHeader>
          <Heading variant="h2">Adviser details</Heading>
          <ResponsiveActions>
            <DownloadStatementMenu
              title="Download adviser statement"
              loading={isExporting}
              onDownloadCurrentValuation={handleAdviserStatement}
              onDownloadEarlierValuation={handleAdviserStatement}
            />

            {canEditAdviser && (
              <Link to={createAdviserEditRoute(id)}>
                <Button variant="outlined" color="primary" size="small">
                  Edit adviser details
                </Button>
              </Link>
            )}

            {impersonatePermission && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleImpersonation}
              >
                View adviser portal
              </Button>
            )}
          </ResponsiveActions>
        </PageHeader>

        <RecordView>
          <AdviserBasicDetails adviserData={adviserData} firmId={firmId} />
          <AdviserFeeAmendments
            changeToInitialInvestmentFees={
              adviserData.changeToInitialInvestmentFees
            }
            commissionOnInitialInvestmentFees={
              adviserData.commissionOnInitialInvestmentFees
            }
            firmsInFeeAmendments={adviserData.firmsInFeeAmendments}
          />
        </RecordView>

        <Divider style={{ margin: '2rem 0' }} />

        <RecordView>
          <OrganisationDetails org={adviserData.organisation} />
        </RecordView>

        <Divider style={{ margin: '2rem 0' }} />

        <AdviserInvestments adviserId={id} adviserName={adviserData.fullName} />

        <Divider style={{ margin: '2rem 0' }} />

        <Notes relationId={id} noteRelation={NoteRelation.Adviser} />

        {impersonatePermission && isSuperAdmin && (
          <AlertDialog
            open={impersonationDisclosure.isOpen}
            onClose={impersonationDisclosure.onClose}
            title="Choose which firms portal to view"
            content={
              <FirmSelector
                onChange={(_firmId, firm) => {
                  if (!firm?.domain) {
                    return;
                  }
                  launchPortalImpersonation.adviser({
                    adviserId: id,
                    firmDomain: firm.domain,
                  });
                  impersonationDisclosure.onClose();
                }}
              />
            }
            btnLabels={{ cancel: 'Close' }}
          />
        )}
      </PageContent>
    </PageContainer>
  );
};

export default ViewAdviser;
